import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { caretPosForDigitCount, digitCountBefore, isAmountChangeAllowed, isPasteableAmount, stripAmountComma } from './utils';

/**
 * 금액 입력(콤마 표시)의 onChange 처리 + 커서 복원을 한 곳에 모은 훅.
 *
 * 화면마다 같은 식을 복사하면 조용히 낡아서 화면별로 결과가 갈린다. 거부 규칙
 * (`isAmountInputAllowed`)과 커서 계산은 여기 한 번만 둔다.
 *
 * 공용 "컴포넌트"가 아니라 훅이다. 마크업·검증은 각 화면이 그대로 갖고 있고,
 * 이 훅은 "입력 문자열 → 콤마 없는 숫자 문자열" 변환과 커서만 책임진다.
 *
 * 리스트 안의 입력(상품별 금액, 강사별 한도)도 그대로 쓸 수 있다. 대상 element 를
 * key 가 아니라 이벤트에서 직접 붙잡아 두기 때문에 ref 등록이 필요 없다.
 */
export const useAmountInputCaret = ({ allowDecimal = false }: { allowDecimal?: boolean } = {}) => {
    const pending = useRef<{ el: HTMLInputElement; digitCount: number } | null>(null);

    /**
     * onChange 공통 처리.
     *
     * @param event    입력 이벤트
     * @param prevText 현재 state 에 들어 있는 값(콤마 없는 숫자 문자열)
     * @returns 통과하면 콤마 없는 숫자 문자열, **거부면 `null`**
     *
     * 원칙 1 — 잘못된 입력은 "지우지" 않고 거부한다. `replace(/[^0-9]/g,'')` 는
     * 실패 대신 다른 유효 금액을 만든다(`-100`→100, `12.34`→1234, `1e3`→13).
     * 여기서는 패턴에 안 맞으면 `null` 을 돌려주고, 호출부는 state 를 아예 건드리지 않는다.
     */
    const changeAmount = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, prevText: string | number): string | null => {
            const raw = event.target.value;
            // 입력 출처로 잣대를 나눈다. onPaste/onDrop 은 클립보드만 보므로
            // 자동완성·제안 교체·음성 입력은 그 검사를 우회한다.
            if (!isAmountChangeAllowed(raw, (event.nativeEvent as InputEvent)?.inputType, prevText, allowDecimal))
                return null;

            const digits = stripAmountComma(raw);
            const caret = event.target.selectionStart;
            /*
             * - selectionStart 가 null 이면 복원을 건너뛴다. 끝으로 보내지 않는다.
             * - 자릿수가 그대로면(콤마만 눌렀을 때) setState 가 bail out 해서 리렌더가
             *   없다. 그때 pending 을 남겨두면 한참 뒤 엉뚱한 렌더에 적용되므로 기억하지 않는다.
             */
            pending.current =
                caret == null || digits === stripAmountComma(prevText)
                    ? null
                    : { el: event.target, digitCount: digitCountBefore(raw, caret) };
            return digits;
        },
        [allowDecimal],
    );

    /**
     * 붙여넣기·드롭 차단.
     *
     * 타이핑 경로는 `1,000` 에서 한 글자 지운 `1,00` 같은 편집 중간 상태를 허용해야 해서
     * 콤마 위치를 못 본다. 그런데 붙여넣기는 한 번에 값이 확정되므로 같은 잣대를 쓰면
     * `10000,20000` 같은 CSV 조각이 통과해 `1000020000` 이라는 **다른 금액**이 된다.
     * 기프티콘 한도처럼 자동 저장되는 화면에서는 사용자가 알아채기 전에 서버로 넘어간다.
     *
     * 그래서 여기서만 정규형(`1,234,567` 또는 `1234567`)을 강제한다.
     * 통과하면 그대로 흘려보내고 `changeAmount` 가 받아 재포맷한다.
     */
    const pasteAmount = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
        if (!isPasteableAmount(event.clipboardData.getData('text'), allowDecimal)) event.preventDefault();
    }, [allowDecimal]);

    /** 드롭도 붙여넣기와 같은 경로다. */
    const dropAmount = useCallback((event: React.DragEvent<HTMLInputElement>) => {
        if (!isPasteableAmount(event.dataTransfer.getData('text'), allowDecimal)) event.preventDefault();
    }, [allowDecimal]);

    /** 부모·서버가 값을 갈아끼울 때 호출해 낡은 커서 복원을 버린다. */
    const dropPendingCaret = useCallback(() => {
        pending.current = null;
    }, []);

    useLayoutEffect(() => {
        const next = pending.current;
        // 적용 여부와 무관하게 반드시 비운다. 남겨두면 rollback 렌더에 낡은 커서가 적용된다.
        pending.current = null;
        if (!next) return;
        // 포커스를 잃었거나(다른 곳 클릭) 언마운트된 입력에는 손대지 않는다.
        if (document.activeElement !== next.el) return;
        const pos = caretPosForDigitCount(next.el.value, next.digitCount);
        next.el.setSelectionRange(pos, pos);
    });

    /*
     * 반환 객체의 신원을 고정한다. 매 렌더마다 새 객체를 돌려주면 이 훅을 의존성 배열에
     * 넣은 useEffect 가 렌더마다 다시 돌아 프리필이 사용자의 입력을 계속 덮어쓴다.
     */
    return useMemo(
        () => ({ changeAmount, dropPendingCaret, pasteAmount, dropAmount }),
        [changeAmount, dropPendingCaret, pasteAmount, dropAmount],
    );
};
